/**
 * English plurals for the two vocabularies the deliverable copy speaks in:
 * packaging SKU names ("10 Paper Food Cups with Lid (8 oz)") and recipe count
 * units ("698 mini muffins"). Pure, no dependencies, unit-tested.
 *
 * The packaging table is CURATED for the SKU names in production (16 names on
 * every root package line since 2026-08-15) because a container name has its
 * noun in the middle ("Container with Clear Lid (24 oz)") and a parenthetical
 * that must never be pluralized. The heuristic below the table exists for a
 * name the table has not met yet: it pluralizes the word before the first
 * " with ", each side of an " & ", and never touches a "(...)" tail.
 */

const PACKAGING_PLURALS: Readonly<Record<string, string>> = {
  'Bulk Tray': 'Bulk Trays',
  'Standard Catering Pan': 'Standard Catering Pans',
  'Aluminum Pan': 'Aluminum Pans',
  'Bulk Delivery Box': 'Bulk Delivery Boxes',
  'Paper Food Cup with Lid (8 oz)': 'Paper Food Cups with Lid (8 oz)',
  'Paper Food Cup with Lid (12 oz)': 'Paper Food Cups with Lid (12 oz)',
  'Container with Clear Lid (24 oz)': 'Containers with Clear Lid (24 oz)',
  '1-Compartment Container (Standard)': '1-Compartment Containers (Standard)',
  '2-Compartment Container (9x6x3 in)': '2-Compartment Containers (9x6x3 in)',
  '3-Compartment Plate (9" Foam)': '3-Compartment Plates (9" Foam)',
  'Kraft Sandwich Bag': 'Kraft Sandwich Bags',
  'Foil Sandwich Bag': 'Foil Sandwich Bags',
  'Plastic Sealable Bag (Sandwich)': 'Plastic Sealable Bags (Sandwich)',
  'Plastic Sealable Bag (5x5)': 'Plastic Sealable Bags (5x5)',
  'Paper Lunch Bag (3x2x7 in)': 'Paper Lunch Bags (3x2x7 in)',
  'Condiment Cup & Lid': 'Condiment Cups & Lids',
  'Parchment & Foil Wrap': 'Parchment & Foil Wraps',
  'Spork & Napkin': 'Sporks & Napkins',
}

/** Words that are already plural, or invariant, and must not grow an "s". */
const INVARIANT_WORDS: ReadonlySet<string> = new Set([
  'foil',
  'parchment',
  'plastic',
  'paper',
  'kraft',
  'bulk',
  'standard',
])

/**
 * Units that are a tally, not a thing: "1 each", "1 serving", "2 Quantity".
 * The copy drops these and lets the item name carry the noun ("355 Animal
 * Crackers, Bag (approx. 0.5 oz)"), because "355 eaches" helps nobody.
 */
const GENERIC_COUNT_UNITS: ReadonlySet<string> = new Set([
  'each',
  'ea',
  'x',
  'serving',
  'servings',
  'quantity',
  'qty',
  'unit',
  'units',
  'package',
  'packages',
  'pkg',
  'pc',
  'pcs',
  'ct',
  'count',
  'portion',
  'portions',
  'item',
  'items',
])

export function isGenericCountUnit(unit: string): boolean {
  return GENERIC_COUNT_UNITS.has(unit.trim().toLowerCase())
}

/** Regular English pluralization of ONE word, preserving its capitalization. */
export function pluralizeWord(word: string): string {
  if (word.length === 0) {
    return word
  }
  const lower = word.toLowerCase()
  if (INVARIANT_WORDS.has(lower)) {
    return word
  }
  if (isPluralWord(word)) {
    return word
  }
  if (/(s|x|z|ch|sh)$/i.test(word)) {
    return `${word}es`
  }
  if (/[^aeiou]y$/i.test(word)) {
    return `${word.slice(0, -1)}ies`
  }
  return `${word}s`
}

/**
 * Does this word already read as a plural? Recipe authors type units in both
 * numbers ("Sandwich" and "Sandwiches", "Pancakes", "Wedges", "Links", "Tacos"),
 * so a unit that ends in "s" and is not a known singular-in-s stays as it is.
 */
export function isPluralWord(word: string): boolean {
  const lower = word.toLowerCase()
  if (SINGULAR_IN_S.has(lower)) {
    return false
  }
  return /[^s]s$/.test(lower) || /(ches|shes|xes|zes|sses)$/.test(lower)
}

/** Singular nouns that happen to end in "s". */
const SINGULAR_IN_S: ReadonlySet<string> = new Set(['glass', 'hummus', 'couscous', 'bus', 'gas', 'lens'])

/**
 * Plural of a multi-word count noun: the LAST word carries the number
 * ("Mini Muffin" → "Mini Muffins", "Whole Apple" → "Whole Apples", "Hot Dog" →
 * "Hot Dogs"). `count` of exactly one keeps the singular.
 */
export function pluralizeNoun(noun: string, count = 2): string {
  const trimmed = noun.trim()
  if (count === 1 || trimmed.length === 0) {
    return trimmed
  }
  const words = trimmed.split(/\s+/)
  const last = words[words.length - 1]!
  return [...words.slice(0, -1), pluralizeWord(last)].join(' ')
}

/**
 * Plural form of a packaging SKU name for a count above one. Curated table
 * first, heuristic second. `count` of exactly one returns the name unchanged.
 */
export function pluralizePackagingName(name: string, count = 2): string {
  const trimmed = name.trim()
  if (count === 1 || trimmed.length === 0) {
    return trimmed
  }
  const curated = PACKAGING_PLURALS[trimmed]
  if (curated) {
    return curated
  }
  const { head, tail } = splitPackagingName(trimmed)
  return `${pluralizeHead(head)}${tail}`
}

/**
 * A packaging name's head (where its noun is) and its tail: everything from the first comma or " (" on. A size or
 * material there never pluralizes: "Full Hotel Pan, 2.5"" → "Full Hotel Pan" + ", 2.5"" (Matt's pan names, D39, D51),
 * "Hoagie Box (12 in)" → "Hoagie Box" + " (12 in)".
 */
function splitPackagingName(name: string): { head: string; tail: string } {
  const cutAt = name.search(/,|\s\(/)
  return cutAt === -1 ? { head: name, tail: '' } : { head: name.slice(0, cutAt), tail: name.slice(cutAt) }
}

function pluralizeHead(head: string): string {
  // "Cup with Lid" → the noun is the word before " with ".
  const withAt = head.search(/\swith\s/i)
  if (withAt !== -1) {
    const before = head.slice(0, withAt)
    const after = head.slice(withAt)
    return `${pluralizeNoun(before)}${after}`
  }
  // "Condiment Cup & Lid" → both nouns pluralize.
  if (/\s&\s/.test(head)) {
    return head
      .split(/\s&\s/)
      .map((part) => pluralizeNoun(part))
      .join(' & ')
  }
  return pluralizeNoun(head)
}

/**
 * "Bulk Tray(s)", "Bulk Delivery Box(es)", "Condiment Cup(s) & Lid(s)": the
 * form for a vessel whose COUNT Ordo does not know (bulk service has no pan
 * map). Every word the plural changes gets its own "(suffix)", so a two-noun
 * name marks both nouns and an irregular plural shows its whole ending.
 */
export function optionalPlural(name: string): string {
  const singular = name.trim()
  const plural = pluralizePackagingName(singular, 2)
  if (plural === singular) {
    return singular
  }
  // The tail (a size after a comma, a "(...)") never changes: mark the words of the head only.
  const one = splitPackagingName(singular)
  const many = splitPackagingName(plural)
  const sameTail = one.tail === many.tail
  const tail = sameTail ? one.tail : ''
  const singularWords = (sameTail ? one.head : singular).split(' ')
  const pluralWords = (sameTail ? many.head : plural).split(' ')
  if (singularWords.length !== pluralWords.length) {
    return `${sameTail ? one.head : singular}(s)${tail}`
  }
  return `${singularWords
    .map((word, i) => {
      const pluralWord = pluralWords[i]!
      if (pluralWord === word) {
        return word
      }
      if (pluralWord.startsWith(word)) {
        return `${word}(${pluralWord.slice(word.length)})`
      }
      // "Patty" → "Patties": no common stem to hang a suffix on; say both.
      return `${word}(${pluralWord})`
    })
    .join(' ')}${tail}`
}
